import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { combinedPropertyAal, type PortfolioHazardAal } from './portfolioAal'

type Props = { hazards: PortfolioHazardAal[]; propertyId: number; propertyName: string; currency: string }

export default function PropertyAalMetadata({ hazards, propertyId, propertyName, currency }: Props) {
  const money = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
  const amount = (value: number | null) => (value == null ? '—' : money.format(value))
  const contributors = hazards.filter(
    hazard =>
      hazard.modelled &&
      hazard.periodAvailable &&
      !hazard.loading &&
      !hazard.error &&
      hazard.byProperty[propertyId]?.combined != null,
  )

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" fontWeight={600}>
        {propertyName}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Annualised property damage and business-interruption (BI) losses.
      </Typography>
      <TableContainer>
        <Table size="small" aria-label="Property annual loss breakdown" sx={{ '& .MuiTableCell-root': { px: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Hazard</TableCell>
              <TableCell align="right">Damage AAL</TableCell>
              <TableCell align="right">BI AAL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contributors.map(hazard => {
              const value = hazard.byProperty[propertyId]
              return (
                <TableRow key={hazard.hazard.name}>
                  <TableCell>{hazard.hazard.display_name}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {amount(value.damage)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {amount(value.bi)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
          <TableFooter>
            <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 800, color: 'text.primary' } }}>
              <TableCell colSpan={2}>Combined AAL</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {amount(combinedPropertyAal(hazards, propertyId))}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary">
        All amounts are per year. Hazard estimates are summed independently.
      </Typography>
    </Stack>
  )
}
